<template>
<div class="quick-publish">
  <!-- 主内容区：PC 左右分栏，手机上下堆叠 -->
  <div class="qp-main">
    <!-- 左侧：视频预览 + 上传 -->
    <div class="qp-left">
      <!-- 视频上传区 -->
      <div v-if="!quickFileName" class="qp-upload-zone" @click="triggerUpload">
        <div class="qp-upload-icon">🎬</div>
        <div class="qp-upload-text">点击或拖拽视频到此处</div>
        <div class="qp-upload-tip">支持 MP4 / MOV，建议 &lt; 100MB</div>
        <div class="qp-upload-asr-note">🧠 上传后自动 AI 识别视频语音，生成标题/描述/话题</div>
        <input ref="fileInputRef" type="file" accept="video/*" @change="onQuickFile" hidden />
      </div>

      <!-- 视频预览 + 封面预览 -->
      <div v-else class="qp-preview-card">
        <!-- 视频预览 -->
        <div class="qp-video-preview" @click="quickPreviewFullscreen = true">
          <video v-if="quickPreviewIsVideo && quickPreviewSrc" :src="quickPreviewSrc" muted playsinline class="qp-video-thumb" />
          <img v-else-if="!quickPreviewIsVideo && quickPreviewSrc" :src="quickPreviewSrc" class="qp-video-thumb" />
          <div class="qp-video-play">▶</div>
        </div>

        <!-- 封面预览 -->
        <div class="qp-cover-section">
          <div class="qp-cover-label">🖼️ 封面图</div>
          <div v-if="coverPreviewSrc" class="qp-cover-preview">
            <img :src="coverPreviewSrc" class="qp-cover-thumb" />
            <div class="qp-cover-actions">
              <el-button size="mini" @click="openCoverDialog" :disabled="coverExtracting">🖼️ 选帧</el-button>
              <el-button size="mini" @click="triggerCoverUpload">📁 上传</el-button>
              <el-button size="mini" plain @click="quickPreviewFullscreen = true">🔍 放大</el-button>
            </div>
          </div>
          <div v-else class="qp-cover-placeholder">
            <div class="qp-cover-loading" v-if="coverExtracting">⏳ 正在提取封面...</div>
            <div class="qp-cover-auto" v-else>✅ 已自动提取</div>
          </div>
          <input ref="coverInputRef" type="file" accept="image/*" @change="onCoverFile" hidden />
        </div>

        <!-- 文件信息 -->
        <div class="qp-file-info">
          <div class="qp-file-status">✅ 已上传</div>
          <div class="qp-file-name" :title="quickFileName">{{ quickFileName }}</div>
          <el-button size="mini" plain @click="clearQuickFile">🗑️ 删除</el-button>
        </div>
      </div>

      <!-- AI 语音识别（上传时自动触发） -->
      <div v-if="quickFileName && asrLoading" class="qp-asr-section">
        <div class="qp-asr-title">🧠 AI 正在识别视频语音 · ⏱ 已用时 {{ asrElapsed }} 秒</div>
        <div class="qp-asr-stage">{{ asrStage || '正在准备…' }}</div>
        <div class="qp-asr-bar">
          <div class="qp-asr-bar-inner" :style="{ width: (asrProgress * 100).toFixed(1) + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- 右侧：表单区 -->
    <div class="qp-right">
      <!-- 标题 -->
      <div class="qp-form-group">
        <label class="qp-form-label">🎯 标题 <span class="qp-form-count">{{ quickForm.title.length }}/30</span></label>
        <div class="qp-input-wrap">
          <el-input v-model="quickForm.title" type="textarea" :rows="2" :maxlength="30" show-word-limit
                    placeholder="AI 生成或手写，带钩子吸引人点击" />
          <el-button size="small" class="qp-ai-btn" :loading="_rwBusy==='quickForm.title'"
                     @click="aiRewrite('quickForm.title', quickForm.title, 'title')">✨ AI<span v-if="_rwBusy==='quickForm.title'" class="qp-ai-busy"><i class="qp-ai-spin"></i>生成中…</span></el-button>
        </div>
      </div>

      <!-- 描述 -->
      <div class="qp-form-group">
        <label class="qp-form-label">📝 描述 <span class="qp-form-count">{{ quickDesc.length }}/1000</span></label>
        <div class="qp-input-wrap">
          <el-input v-model="quickDesc" type="textarea" :rows="4" :maxlength="1000" show-word-limit
                    placeholder="视频文案，口语化，含行动引导（关注/咨询）" />
          <el-button size="small" class="qp-ai-btn" :loading="_rwBusy==='quickDesc'"
                     @click="aiRewrite('quickDesc', quickDesc, 'desc')">✨ AI<span v-if="_rwBusy==='quickDesc'" class="qp-ai-busy"><i class="qp-ai-spin"></i>生成中…</span></el-button>
        </div>
      </div>

      <!-- 话题标签 -->
      <div class="qp-form-group">
        <label class="qp-form-label">🏷️ 话题标签 <span class="qp-form-count">{{ tagsList.length }}/10</span></label>
        <div class="qp-tags-input">
          <el-tag v-for="(tag, idx) in tagsList" :key="idx" closable @close="removeTag(idx)" class="qp-tag">
            #{{ tag }}
          </el-tag>
          <el-input v-model="newTagInput" size="small" placeholder="输入标签后回车" @keyup.enter="addTag" class="qp-tag-input" />
          <el-button size="small" :loading="tagAutoLoading" @click="autoGenTags('', false)">✨ 自动话题</el-button>
          <span class="qp-inline-note">🏷️ 按文案智能推荐</span>
        </div>
      </div>

      <!-- 发布设置 -->
      <div class="qp-form-group">
        <label class="qp-form-label">📅 发布设置</label>
        <!-- 第1排：发布平台 + 发布方式 -->
        <div class="qp-setting-row1">
          <div class="qp-setting-item">
            <label>🌍 发布平台</label>
            <el-select v-model="publishPlatform" size="small" class="qp-setting-select" @change="onPlatformChange">
              <el-option label="抖音" value="douyin" />
              <el-option label="快手" value="kuaishou" />
              <el-option label="小红书" value="xhs" />
            </el-select>
          </div>
          <div class="qp-setting-item">
            <label>发布方式</label>
            <el-select v-model="quickForm.mode" size="small" class="qp-setting-select">
              <el-option label="立即发布" value="auto" />
              <el-option label="定时发布" value="scheduled" />
            </el-select>
          </div>
          <div class="qp-setting-item" v-if="quickForm.mode === 'scheduled'">
            <label>发布时间</label>
            <el-date-picker v-model="quickForm.scheduled_at" type="datetime" size="small"
                            placeholder="选择发布时间" class="qp-setting-date" />
          </div>
        </div>
        <!-- 第2排：目标账号（独占一整排，多选名称全部可见） -->
        <div class="qp-account-row">
          <label class="qp-account-label">👥 目标账号（可多选，一键群发）
            <span class="qp-account-hint" v-if="filteredAccounts.length === 0">{{ accountHintText }}</span>
            <span class="qp-account-hint" v-else>{{ selectedAccountIds.length }} / {{ filteredAccounts.length }} 个已选</span>
          </label>
          <el-select v-model="selectedAccountIds" multiple size="small" class="qp-account-select"
                     placeholder="选择发布账号，可多选一键群发">
            <el-option v-for="acc in filteredAccounts" :key="acc.id || acc.platform_account_id" :value="acc.id || acc.platform_account_id"
                       :label="acc.name">
              <div class="qp-acc-opt">
                <img v-if="acc.avatar" :src="acc.avatar" class="qp-acc-avatar" alt="" />
                <span v-else class="qp-acc-avatar qp-acc-avatar-fallback">📱</span>
                <span class="qp-acc-opt-name">{{ acc.name }}</span>
                <span class="qp-acc-opt-plat">{{ acc.platformCn || '抖音' }}</span>
              </div>
            </el-option>
          </el-select>
        </div>
        <!-- 第3排：发布权限 -->
        <div class="qp-permission-row">
          <label class="qp-permission-label">🔒 发布权限</label>
          <div class="qp-permission-options">
            <div class="qp-permission-opt" :class="{ active: quickForm.permission === 'public' }" @click="quickForm.permission = 'public'">
              <span class="qp-permission-icon">🌍</span>
              <span class="qp-permission-name">公开</span>
            </div>
            <div class="qp-permission-opt" :class="{ active: quickForm.permission === 'friends' }" @click="quickForm.permission = 'friends'">
              <span class="qp-permission-icon">👥</span>
              <span class="qp-permission-name">好友可见</span>
            </div>
            <div class="qp-permission-opt" :class="{ active: quickForm.permission === 'private' }" @click="quickForm.permission = 'private'">
              <span class="qp-permission-icon">🔒</span>
              <span class="qp-permission-name">仅自己可见</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 位置信息（GPS 自动定位后续再接） -->
      <div class="qp-setting-group">
        <label>📍 位置信息（可选）</label>
        <el-input v-model="quickForm.location" type="textarea" :rows="1" maxlength="40" show-word-limit
                  placeholder="手动输入位置，如：福建省厦门市集美区……" class="qp-location-input" />
      </div>

      <!-- 提交按钮 -->
      <div class="qp-submit-section">
        <el-button type="primary" size="large" class="qp-submit-btn" :loading="submitting" :disabled="!quickFileName || asrLoading"
                   @click="submitPublish">🚀 立即发布</el-button>
        <el-button size="large" class="qp-submit-btn qp-btn-queue" :disabled="!quickFileName || asrLoading"
                   @click="submitQueue">📥 暂存发布队列</el-button>
      </div>
    </div>
  </div>

  <!-- 全屏预览弹窗 -->
  <div v-if="quickPreviewFullscreen" class="qp-preview-modal" @click="quickPreviewFullscreen = false">
    <div class="qp-preview-modal-content" @click.stop>
      <video v-if="quickPreviewIsVideo && quickPreviewSrc" :src="quickPreviewSrc" controls autoplay class="qp-preview-video" />
      <img v-else-if="coverPreviewSrc" :src="coverPreviewSrc" class="qp-preview-cover" />
      <button class="qp-preview-close" @click="quickPreviewFullscreen = false">✕</button>
    </div>
  </div>

  <!-- 封面选帧弹窗（对标抖音多帧选封面） -->
  <div v-if="coverDialogVisible" class="qp-preview-modal" @click="coverDialogVisible = false">
    <div class="qp-cover-dialog" @click.stop>
      <div class="qp-cover-dialog-title">🖼️ 选择封面</div>
      <div class="qp-cover-frame-grid">
        <div v-for="(f, i) in coverFrames" :key="i" class="qp-cover-frame-item"
             :class="{ active: coverAssetPath && coverAssetPath === f.cover_path }" @click="pickCoverFrame(f)">
          <img :src="f.cover_base64" class="qp-cover-frame-img" />
          <div class="qp-cover-frame-time">⏱ {{ f.seek }}s</div>
          <div v-if="coverAssetPath && coverAssetPath === f.cover_path" class="qp-cover-frame-check">✓ 当前</div>
        </div>
      </div>
      <div class="qp-cover-dialog-foot">
        <el-button size="small" @click="triggerCoverUpload">📁 上传自定义封面</el-button>
        <el-button size="small" type="primary" @click="coverDialogVisible = false">完成</el-button>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import api from '@/api'
import { uploadChunked } from '../utils/chunkUpload'

export default {
  name: 'QuickPublish',
  data() {
    return {
      // 文件状态
      quickFileName: '',
      quickPreviewSrc: '',
      quickPreviewIsVideo: true,
      quickPreviewFullscreen: false,
      quickAssetPath: '',
      uploadProgress: null,
      
      // 封面状态
      coverPreviewSrc: '',
      coverExtracting: false,
      coverAssetPath: '',
      coverFrames: [],          // 多帧候选 [{idx,seek,cover_path,cover_base64}]
      coverDialogVisible: false,

      // 标签
      tagsList: [],
      newTagInput: '',
      tagAutoLoading: false,

      // ASR 状态
      asrLoading: false,
      asrStage: '',
      asrProgress: 0,
      asrElapsed: 0,
      asrTimer: null,

      // 表单数据
      quickForm: {
        title: '',
        desc: '',
        mode: 'auto',
        scheduled_at: null,
        location: '',
        permission: 'public'
      },
      quickDesc: '',

      // AI 重写
      _rwBusy: '',
      
      // 提交状态
      submitting: false,

      // 发布平台（2026-09-08 批次31：抖音/快手/小红书；蚁小二暂只接抖音）
      publishPlatform: 'douyin',

      // 账号列表（可多选，2026-09-08 批次 30）
      accounts: [],
      selectedAccountIds: [],
      selectedAccountId: ''
    }
  },
  computed: {
    // 按发布平台过滤账号（账号接口返回中文平台名如「抖音」，统一映射为英文 key 再比较）
    filteredAccounts() {
      const plat = this.publishPlatform || 'douyin'
      return (this.accounts || []).filter(a => this._normPlatform(a.platform) === plat)
    },
    // 账号区空态文案：按平台给出引导（抖音异常=刷新；快手/小红书=去蚁小二工作台绑定）
    accountHintText() {
      if (this.publishPlatform === 'kuaishou') return '暂无快手账号，请先在蚁小二工作台（www.yixiaoer.cn）绑定快手账号后刷新'
      if (this.publishPlatform === 'xhs') return '暂无小红书账号，请先在蚁小二工作台（www.yixiaoer.cn）绑定小红书账号后刷新'
      return '暂无抖音账号，请检查蚁小二授权或稍后刷新'
    }
  },
  created() {
    this.loadAccounts()
  },
  beforeDestroy() {
    if (this.asrTimer) clearInterval(this.asrTimer)
    if (this.quickPreviewSrc) URL.revokeObjectURL(this.quickPreviewSrc)
    if (this.coverPreviewSrc) URL.revokeObjectURL(this.coverPreviewSrc)
  },
  methods: {
    // 平台名归一化：接口返回中文「抖音/快手/小红书」，选择器 value 用 douyin/kuaishou/xhs
    _normPlatform(p) {
      if (!p) return 'douyin'
      const m = { '抖音': 'douyin', '快手': 'kuaishou', '小红书': 'xhs', 'xiaohongshu': 'xhs', 'dy': 'douyin', 'douyin': 'douyin', 'kuaishou': 'kuaishou', 'xhs': 'xhs' }
      return m[p] || p
    },
    // 切换发布平台：清空已选账号；快手/小红书提示绑定引导
    onPlatformChange(v) {
      this.selectedAccountIds = []
      if (v === 'kuaishou') this.$message.info('快手需先在蚁小二工作台绑定账号，绑定后本页即可看到并群发')
      if (v === 'xhs') this.$message.info('小红书需先在蚁小二工作台绑定账号，绑定后本页即可看到并群发')
    },

    // 加载蚁小二账号列表
    async loadAccounts() {
      try {
        const res = await api.get('/publish/yxer/accounts')
        if (res.ok && res.accounts && res.accounts.length > 0) {
          this.accounts = res.accounts
          // 默认选中第一个（不默认全选，避免误发多个账号）
          this.selectedAccountId = res.accounts[0].id || res.accounts[0].platform_account_id
          this.selectedAccountIds = [this.selectedAccountId]
        }
      } catch (e) {
        console.error('加载账号失败:', e)
      }
    },
    
    // 触发文件上传
    triggerUpload() {
      this.$refs.fileInputRef.click()
    },
    
    // 处理文件选择
    async onQuickFile(e) {
      const file = e.target.files[0]
      if (!file) return
      
      this.quickFileName = file.name
      this.quickPreviewSrc = URL.createObjectURL(file)
      this.quickPreviewIsVideo = file.type.startsWith('video/')
      
      // 上传文件到后端
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('account_id', 0)   // 素材库不绑蚁小二账号（后端要求 int，字符串会 422）
        formData.append('title', this.quickForm.title || file.name)
        const res = await api.post('/assets', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000
        })
        if (res.ok) {
          this.quickAssetPath = res.file_path
          // 自动提取封面
          this.extractCover()
          // 自动触发 ASR
          this.runAsr()
        }
      } catch (err) {
        console.error('上传失败:', err)
        // 修复错误信息提取（避免显示 [object Object]）
        let errorMsg = '未知错误'
        if (err.response?.data?.detail) {
          errorMsg = err.response.data.detail
        } else if (err.response?.data?.message) {
          errorMsg = err.response.data.message
        } else if (err.message) {
          errorMsg = err.message
        } else if (err.request) {
          errorMsg = '网络错误，请检查后端服务是否正常运行'
        }
        this.$message.error('视频上传失败: ' + errorMsg)
      }
    },
    
    // 提取封面（多帧候选，frames[0] 即默认封面；对齐后端字段 file_path）
    async extractCover() {
      if (!this.quickAssetPath) return
      this.coverExtracting = true
      try {
        const res = await api.post('/content/cover-frames', {
          file_path: this.quickAssetPath,
          count: 4
        }, { timeout: 60000 })
        if (res.ok && res.frames && res.frames.length > 0) {
          this.coverFrames = res.frames
          this.coverPreviewSrc = res.frames[0].cover_base64
          this.coverAssetPath = res.frames[0].cover_path || ''
        } else {
          this.$message.warning('封面提取失败，可稍后手动上传封面')
        }
      } catch (err) {
        console.error('封面提取失败:', err)
        this.$message.warning('封面提取失败: ' + (err.response?.data?.detail || err.message))
      } finally {
        this.coverExtracting = false
      }
    },

    // 打开封面候选选择框
    openCoverDialog() {
      if (this.coverFrames.length > 0) {
        this.coverDialogVisible = true
        return
      }
      // 没有候选帧 → 先提取再打开
      this.coverExtracting = true
      api.post('/content/cover-frames', { file_path: this.quickAssetPath, count: 4 }, { timeout: 60000 })
        .then(res => {
          if (res.ok && res.frames && res.frames.length > 0) {
            this.coverFrames = res.frames
            this.coverDialogVisible = true
          } else {
            this.$message.warning('暂无法提取封面帧，请手动上传图片封面')
          }
        })
        .catch(err => this.$message.error('封面帧提取失败: ' + (err.response?.data?.detail || err.message)))
        .finally(() => { this.coverExtracting = false })
    },

    // 选择某帧作封面
    pickCoverFrame(f) {
      if (!f) return
      this.coverPreviewSrc = f.cover_base64
      this.coverAssetPath = f.cover_path || ''
      this.coverDialogVisible = false
    },
    
    // 触发封面上传
    triggerCoverUpload() {
      this.$refs.coverInputRef.click()
    },
    
    // 处理封面文件
    onCoverFile(e) {
      const file = e.target.files[0]
      if (!file) return
      if (this.coverPreviewSrc && this.coverPreviewSrc.startsWith('blob:')) {
        URL.revokeObjectURL(this.coverPreviewSrc)
      }
      this.coverPreviewSrc = URL.createObjectURL(file)
      this.coverAssetPath = ''   // 本地上传封面无服务器路径（发布时用视频帧兜底）
      this.coverDialogVisible = false
      e.target.value = ''
    },
    
    // 删除文件
    clearQuickFile() {
      if (this.quickPreviewSrc) URL.revokeObjectURL(this.quickPreviewSrc)
      if (this.coverPreviewSrc) URL.revokeObjectURL(this.coverPreviewSrc)
      this.quickFileName = ''
      this.quickPreviewSrc = ''
      this.quickAssetPath = ''
      this.coverPreviewSrc = ''
      this.coverAssetPath = ''
      this.quickForm.title = ''
      this.quickDesc = ''
      this.tagsList = []
    },
    
    // 运行 ASR
    async runAsr() {
      if (!this.quickAssetPath) return
      this.asrLoading = true
      this.asrElapsed = 0
      this.asrStage = '正在提交识别任务...'
      
      // 启动计时器
      this.asrTimer = setInterval(() => {
        this.asrElapsed++
      }, 1000)
      
      try {
        // 提交 ASR 任务（后端字段是 file_path）
        const submitRes = await api.post('/content/video-asr', {
          file_path: this.quickAssetPath,
          industry: '数码打印'
        }, { timeout: 30000 })
        
        if (submitRes.ok && submitRes.task_id) {
          this.asrStage = '正在识别语音...'
          // 开始轮询
          this.pollAsrTask(submitRes.task_id)
        }
      } catch (err) {
        console.error('ASR 提交失败:', err)
        this.$message.error('语音识别提交失败')
        this.stopAsr()
      }
    },
    
    // 轮询 ASR 任务
    async pollAsrTask(taskId, count = 0) {
      if (count > 100) {
        this.$message.warning('语音识别超时')
        this.stopAsr()
        return
      }
      
      await new Promise(r => setTimeout(r, 3000))
      
      try {
        const res = await api.get(`/content/video-asr/${taskId}`)
        
        if (res.ok) {
          this.asrProgress = res.progress || 0
          this.asrStage = res.stage || this.asrStage
          
          if (res.status === 'done') {
            this.asrProgress = 100
            this.asrStage = '识别完成！'
            this.stopAsr()
            this.applyAsrResult(res)
          } else if (res.status === 'failed') {
            this.$message.error('语音识别失败: ' + res.error)
            this.stopAsr()
          } else {
            this.pollAsrTask(taskId, count + 1)
          }
        } else {
          this.pollAsrTask(taskId, count + 1)
        }
      } catch (err) {
        this.pollAsrTask(taskId, count + 1)
      }
    },
    
    // 停止 ASR
    stopAsr() {
      this.asrLoading = false
      if (this.asrTimer) {
        clearInterval(this.asrTimer)
        this.asrTimer = null
      }
    },
    
    // 应用 ASR 结果
    applyAsrResult(res) {
      if (res.transcript) {
        this.quickDesc = res.transcript
        // 自动提取标题
        if (!this.quickForm.title) {
          this.quickForm.title = res.transcript.substring(0, 30).replace(/[#\n]/g, '')
        }
        // 自动推荐话题标签（AI 生成 #关键词，2026-09-08）
        if (res.tags && res.tags.length > 0) {
          this.tagsList = res.tags
        }
        this.autoGenTags(res.transcript)
        this.$message.success('AI 文案已生成！')
      }
    },

    // AI 自动生成 #话题标签（后端 /content/recommend-tags）
    async autoGenTags(text, silent = true) {
      const src = (text || this.quickDesc || '').trim()
      if (src.length < 4 || this.tagAutoLoading) return
      this.tagAutoLoading = true
      try {
        const res = await api.post('/content/recommend-tags', {
          text: src.slice(0, 800),
          industry: '数码打印',
          count: 5
        }, { timeout: 90000 })
        if (res.ok && res.tags && res.tags.length > 0) {
          // 只补空/不足：保留用户手动加的
          const merged = this.tagsList.slice()
          for (const t of res.tags) {
            if (!merged.includes(t) && merged.length < 10) merged.push(t)
          }
          this.tagsList = merged
          if (!silent) this.$message.success('已生成 ' + res.tags.length + ' 个话题标签')
        } else if (!silent) {
          this.$message.warning(res.message || '话题生成失败，请手动添加')
        }
      } catch (err) {
        if (!silent) this.$message.error('话题生成失败: ' + (err.response?.data?.detail || err.message))
      } finally {
        this.tagAutoLoading = false
      }
    },
    
    // AI 重写
    async aiRewrite(field, currentVal, role) {
      if (!currentVal.trim()) {
        this.$message.warning('请先输入内容或等待 AI 生成')
        return
      }
      this._rwBusy = field
      try {
        const res = await api.post('/assistant/rewrite', {
          text: currentVal,
          role: role,
          industry: '数码打印',
          seed: Math.floor(Math.random() * 1000)
        }, { timeout: 90000 })
        
        if (res.ok && res.text) {
          if (field === 'quickForm.title') {
            this.quickForm.title = res.text
          } else if (field === 'quickDesc') {
            this.quickDesc = res.text
          }
        }
      } catch (err) {
        this.$message.error('AI 重写失败: ' + (err.response?.data?.detail || err.message))
      } finally {
        this._rwBusy = ''
      }
    },
    
    // 添加标签
    addTag() {
      const tag = this.newTagInput.trim()
      if (tag && !this.tagsList.includes(tag) && this.tagsList.length < 10) {
        this.tagsList.push(tag)
        this.newTagInput = ''
      }
    },
    
    // 移除标签
    removeTag(idx) {
      this.tagsList.splice(idx, 1)
    },
    
    // 构建发布描述：#话题直接拼进描述正文（抖音话题必须在文案里才生效，2026-09-08 批次 30）
    buildPublishDesc() {
      let desc = (this.quickDesc || '').trim()
      const tags = this.tagsList.slice(0, 10)
      if (tags.length > 0) {
        const hashtagLine = tags.map(t => '#' + t.trim()).join(' ')
        desc = desc ? `${desc}\n${hashtagLine}` : hashtagLine
      }
      if (desc.length > 950) desc = desc.slice(0, 950)
      return desc
    },

    // 公共提交：多账号循环建任务（一键群发）→ 返回 [成功数, 总数]；onOk 成功后回调
    async _doSubmit(mode, scheduledAt, onOk) {
      if (!this.quickFileName) { this.$message.warning('请先上传视频'); return false }
      if (this.asrLoading) { this.$message.warning('AI 识别中，请稍候'); return false }
      if (!this.quickForm.title.trim()) { this.$message.warning('请填写标题'); return false }
      let ids = (this.selectedAccountIds && this.selectedAccountIds.length)
        ? this.selectedAccountIds.slice()
        : (this.selectedAccountId ? [this.selectedAccountId] : [])
      ids = ids.filter(Boolean)
      if (ids.length === 0) { this.$message.warning('请选择目标账号'); return false }

      this.submitting = true
      let success = 0
      let failMsg = ''
      try {
        const payloadBase = {
          account_id: 0,
          platform: this.publishPlatform || 'douyin',
          file_path: this.quickAssetPath,
          media_type: 'video',
          engine: 'yxer',
          title: this.quickForm.title.trim().slice(0, 30),
          desc: this.buildPublishDesc(),
          tags: this.tagsList.slice(0, 10).join(','),
          mode,
          scheduled_at: scheduledAt || '',
          location: this.quickForm.location || '',
          permission: this.quickForm.permission || 'public',
          cover_path: this.coverAssetPath || ''
        }
        const results = await Promise.allSettled(ids.map(aid =>
          api.post('/publish', Object.assign({}, payloadBase, { yxer_account_id: aid }), { timeout: 30000 })
        ))
        results.forEach(r => {
          if (r.status === 'fulfilled' && r.value && r.value.ok) success += 1
          else if (r.status === 'rejected') {
            failMsg = r.reason?.response?.data?.detail || r.reason?.message || '未知错误'
          } else if (r.value && !r.value.ok) {
            failMsg = r.value.message || '未知错误'
          }
        })
      } catch (err) {
        failMsg = err.response?.data?.detail || err.message
      } finally {
        this.submitting = false
      }

      if (success === 0) {
        this.$message.error('提交失败: ' + (failMsg || '请稍后重试'))
        return false
      }
      if (success < ids.length) {
        this.$message.warning('成功 ' + success + '/' + ids.length + ' 个账号，部分失败：' + (failMsg || '请到发布队列查看'))
      } else {
        this.$message.success('成功提交 ' + success + ' 个账号！')
      }
      if (typeof onOk === 'function') onOk()
      return true
    },

    // 提交发布（立即发布）
    async submitPublish() {
      await this._doSubmit('auto', this.quickForm.mode === 'scheduled' ? this.quickForm.scheduled_at : '', () => {
        this.clearQuickFile()
      })
    },

    // 暂存发布队列（提交后保留当前内容，可继续修改/再次提交；2026-09-08 批次 30）
    async submitQueue() {
      const ok = await this._doSubmit('semi', '', null)
      if (ok) {
        this.$message.success('已暂存到发布队列，当前内容保留可继续编辑')
      }
    }
  }
}
</script>

<style scoped>
.quick-publish {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* 顶部横幅 */
.qp-header {
  text-align: center;
  margin-bottom: 20px;
}

.qp-header h1 {
  font-size: 24px;
  margin: 0 0 8px 0;
  color: #303133;
}

.qp-subtitle {
  font-size: 14px;
  color: #909399;
}

/* 主内容区（2026-09-08 批次30：左右间距拉大，上传框与表单不贴太近） */
.qp-main {
  display: flex;
  gap: 44px;
}

/* 发布设置（批次30：两排布局 + 权限深色） */
.qp-publish-settings {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 4px;
}

.qp-setting-item-grow {
  flex: 1;
  min-width: 240px;
}

.qp-permission-row {
  margin-top: 16px;
}

.qp-permission-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 10px;
}

.qp-permission-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.qp-permission-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid #334155;
  border-radius: 8px;
  color: #cbd5e1;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.qp-permission-opt:hover {
  border-color: #3b82f6;
  color: #fff;
}

.qp-permission-opt.active {
  background: rgba(59, 130, 246, 0.18);
  border-color: #3b82f6;
  color: #fff;
}

/* 发布设置 3 排（批次31） */
.qp-setting-row1 {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 16px;
}

.qp-account-row {
  margin-bottom: 16px;
}

.qp-account-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 8px;
}

.qp-account-hint {
  font-size: 12px;
  font-weight: 400;
  color: #60a5fa;
}

.qp-account-select {
  width: 100%;
}

/* 批次32：目标账号多选框加高 52px，多选标签/多行账号名清晰可见 */
.qp-account-select :deep(.el-select__wrapper),
.qp-account-select :deep(.el-input__wrapper) {
  min-height: 52px;
  padding: 8px 12px;
  align-items: center;
  flex-wrap: wrap;
}

/* 批次33b：账号选项 = 头像 + 昵称 + 平台名（不显示粉丝数；蚁小二 API 无抖音号字段） */
.qp-acc-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}

.qp-acc-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #1e293b;
}

.qp-acc-avatar-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.qp-acc-opt-name {
  flex: 1;
  font-size: 13px;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qp-acc-opt-plat {
  font-size: 11px;
  color: #94a3b8;
  border: 1px solid #334155;
  border-radius: 4px;
  padding: 0 5px;
  line-height: 16px;
  flex-shrink: 0;
}

.qp-account-select .el-tag {
  height: 28px;
  line-height: 26px;
  margin: 3px 6px 3px 0;
}

.qp-account-select .el-select__selected-item,
.qp-account-select .el-input__inner {
  font-size: 14px;
}

/* AI 按钮 loading 显眼（批次31）：转圈加粗 + 生成中文案 */
.qp-ai-btn.is-loading {
  min-width: 92px;
}

.qp-ai-busy {
  font-weight: 700;
  color: #93c5fd;
}

.qp-ai-spin {
  display: inline-block;
  width: 13px;
  height: 13px;
  margin-right: 6px;
  vertical-align: -2px;
  border: 2.5px solid rgba(147, 197, 253, 0.3);
  border-top-color: #93c5fd;
  border-radius: 50%;
  animation: qp-ai-rotate 0.7s linear infinite;
}

@keyframes qp-ai-rotate {
  to { transform: rotate(360deg); }
}

.qp-right .qp-ai-btn .el-loading-spinner {
  font-size: 17px;
}

.qp-right .qp-ai-btn .el-loading-spinner .circular {
  stroke-width: 5;
  stroke: #93c5fd;
}

.qp-right .qp-ai-btn.is-loading .el-loading-spinner {
  width: 100%;
  text-align: center;
}

/* 左侧 */
.qp-left {
  flex: 1;
  min-width: 0;
}

/* 右侧 */
.qp-right {
  flex: 1;
  min-width: 0;
}

/* 上传区（深色适配 2026-09-08 批次 30） */
.qp-upload-zone {
  border: 2px dashed #334155;
  border-radius: 12px;
  padding: 60px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(15, 23, 42, 0.45);
}

.qp-upload-zone:hover {
  border-color: #3b82f6;
  background: rgba(30, 41, 59, 0.7);
}

.qp-upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.qp-upload-text {
  font-size: 16px;
  color: #e2e8f0;
  margin-bottom: 8px;
}

.qp-upload-tip {
  font-size: 12px;
  color: #8fa3bd;
}

.qp-upload-asr-note {
  margin-top: 12px;
  font-size: 12px;
  color: #93c5fd;
}

/* 预览卡片（2026-09-08 批次31：科技黑蓝，与全站深色一致） */
.qp-preview-card {
  background: rgba(15, 23, 42, 0.75);
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.qp-video-preview {
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 0 auto 16px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.qp-video-thumb {
  width: 100%;
  display: block;
  border-radius: 8px;
}

.qp-video-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 36px;
  color: rgba(255,255,255,0.9);
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

/* 封面区域 */
.qp-cover-section {
  margin-bottom: 16px;
}

.qp-cover-label {
  font-size: 14px;
  color: #e2e8f0;
  margin-bottom: 8px;
}

.qp-cover-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.qp-cover-thumb {
  width: 150px;
  max-height: 110px;
  height: auto;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #334155;
  background: #000;
}

.qp-cover-actions {
  display: flex;
  gap: 8px;
}

.qp-cover-placeholder {
  padding: 20px;
  text-align: center;
  background: rgba(15, 23, 42, 0.5);
  border: 1px dashed #334155;
  border-radius: 6px;
}

.qp-cover-loading {
  color: #fbbf24;
}

.qp-cover-auto {
  color: #4ade80;
}

/* 文件信息 */
.qp-file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid #1e293b;
  border-radius: 6px;
}

.qp-file-status {
  color: #4ade80;
  font-size: 14px;
}

.qp-file-name {
  flex: 1;
  font-size: 13px;
  color: #cbd5e1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ASR 进度（2026-09-08 批次31：科技蓝黑） */
.qp-asr-section {
  margin-top: 16px;
  padding: 16px;
  background: rgba(15, 23, 42, 0.75);
  border: 1px solid #1e3a5f;
  border-radius: 8px;
}

.qp-asr-title {
  font-size: 14px;
  color: #60a5fa;
  margin-bottom: 4px;
}

.qp-asr-stage {
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.qp-asr-bar {
  height: 8px;
  background: #1e293b;
  border-radius: 4px;
  overflow: hidden;
}

.qp-asr-bar-inner {
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #3b82f6, #60a5fa);
  border-radius: 4px;
  transition: width 0.3s;
}

/* 表单组 */
.qp-form-group {
  margin-bottom: 20px;
}

.qp-form-label {
  display: block;
  font-size: 14px;
  color: #e2e8f0;
  margin-bottom: 8px;
  font-weight: 500;
}

.qp-form-count {
  font-size: 12px;
  color: #8fa3bd;
  float: right;
}

.qp-setting-group label {
  font-size: 14px;
  font-weight: 500;
  color: #e2e8f0;
  display: block;
  margin-bottom: 4px;
}

.qp-inline-note {
  font-size: 12px;
  color: #94a3b8;
}

.qp-input-wrap {
  position: relative;
}

.qp-input-wrap .qp-ai-btn {
  position: absolute;
  right: 4px;
  bottom: 4px;
}

/* 标签输入 */
.qp-tags-input {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 6px;
}

.qp-tag {
  margin: 2px;
}

.qp-tag-input {
  flex: 1;
  min-width: 120px;
  border: none;
}

.qp-tag-input :deep(.el-input__inner) {
  border: none !important;
  background: transparent;
  color: #e2e8f0;
}

/* 发布设置 */
.qp-publish-settings {
  display: flex;
  gap: 16px;
  align-items: center;
}

.qp-setting-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qp-setting-item label {
  font-size: 13px;
  color: #cbd5e1;
}

.qp-setting-select {
  width: 120px;
}

/* 批次32：平台/发布方式下拉加高，更好点选 */
.qp-setting-select :deep(.el-select__wrapper),
.qp-setting-select :deep(.el-input__wrapper) {
  min-height: 44px;
}

.qp-setting-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qp-setting-item label {
  font-size: 13px;
  color: #94a3b8;
}

.qp-setting-select-multi {
  width: 300px;
  max-width: 100%;
}

.qp-setting-date {
  width: 180px;
}

/* 提交按钮 */
.qp-submit-section {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.qp-submit-btn {
  flex: 1;
  min-height: 48px;
  font-size: 15px;
}

.qp-btn-queue {
  background: rgba(30, 41, 59, 0.95) !important;
  border: 1px solid rgba(56, 189, 248, 0.4) !important;
  color: #93c5fd !important;
}

.qp-btn-queue:hover {
  background: rgba(51, 65, 85, 0.95) !important;
  color: #dbeafe !important;
  border-color: rgba(56, 189, 248, 0.6) !important;
}

/* 全屏预览弹窗 */
.qp-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.qp-preview-modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.qp-preview-video {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
}

.qp-preview-cover {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
}

.qp-preview-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 32px;
  cursor: pointer;
}

/* 封面选帧弹窗（2026-09-08 批次 28） */
.qp-cover-dialog {
  background: #1e293b;
  border-radius: 12px;
  padding: 18px;
  max-width: 560px;
  width: 92vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.qp-cover-dialog-title {
  color: #e2e8f0;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
}

.qp-cover-frame-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 2px;
}

.qp-cover-frame-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s;
  background: #0f172a;
}

.qp-cover-frame-item:hover {
  border-color: rgba(37, 99, 235, 0.5);
}

.qp-cover-frame-item.active {
  border-color: #3b82f6;
}

.qp-cover-frame-img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.qp-cover-frame-time {
  position: absolute;
  left: 6px;
  bottom: 6px;
  background: rgba(0, 0, 0, 0.65);
  color: #e2e8f0;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
}

.qp-cover-frame-check {
  position: absolute;
  right: 6px;
  top: 6px;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
}

.qp-cover-dialog-foot {
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
}

/* 位置输入 */
.qp-location-input {
  width: 100%;
}

.qp-location-input :deep(.el-textarea__inner) {
  min-height: 34px !important;
  font-size: 13px;
  padding: 6px 10px;
}

/* 批次33：话题标签(#酱香白酒…)与位置信息文字标绿——一眼可见发布关键项 */
.qp-tag {
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(34, 197, 94, 0.4);
  color: #4ade80;
  font-weight: 600;
}

.qp-tag .el-tag__close {
  color: #86efac;
}

.qp-tag .el-tag__close:hover {
  background: #4ade80;
  color: #0a0f1a;
}

.qp-location-input :deep(.el-textarea__inner) {
  color: #4ade80;
  caret-color: #4ade80;
}

.qp-location-input :deep(.el-textarea__inner)::placeholder {
  color: #64748b;
}

/* 发布权限 */
.qp-permission-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 8px;
}

.qp-permission-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.qp-permission-opt:hover {
  border-color: rgba(37, 99, 235, 0.5);
  background: rgba(30, 41, 59, 0.7);
}

.qp-permission-opt.active {
  border-color: rgba(37, 99, 235, 0.8);
  background: rgba(37, 99, 235, 0.15);
}

.qp-permission-icon {
  font-size: 20px;
}

.qp-permission-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qp-permission-name {
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
}

.qp-permission-desc {
  font-size: 11px;
  color: #94a3b8;
}

/* 响应式 */
@media (max-width: 768px) {
  .qp-main {
    flex-direction: column;
  }
  
  .qp-submit-section {
    flex-direction: column;
  }
  
  .qp-permission-options {
    grid-template-columns: 1fr;
  }
}
</style>
